import React, { useState, useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Avatar,
  alpha
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Add, Delete, Close, ContentCopy, AppsRounded, Security, Lan } from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUserPermissions from "../../hooks/useUserPermissions";

function ClientsUpdate({ handleModalClose, isNew, clientData, refetch }) {
  const { getAllScopes } = useUserPermissions();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState(null);

  const validateURL = (value) => {
    if (!value) return true;
    try {
      new URL(value);
      return true;
    } catch {
      return "Please enter a valid URL";
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: isNew ? {
      name: "",
      client_type: "confidential",
      logo_url: "",
      privacy_policy_url: "",
      terms_of_service_url: "",
      scopes: [],
      redirect_uris: [""],
      post_logout_redirect_uris: [""],
      backchannel_logout_uri: ""
    } : {
      ...clientData,
      scopes: clientData?.scopes?.split(" ") || [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "redirect_uris" });
  const { fields: logoutFields, append: appendLogout, remove: removeLogout } = useFieldArray({ control, name: "post_logout_redirect_uris" });

  const { isLoading: scopesLoading, data: scopesData } = useQuery(["scopes"], () =>
    axiosPrivate.get(`/oauth/scopes`, { params: { page: -1 } }),
    { refetchOnWindowFocus: false, enabled: !!getAllScopes }
  );

  const { mutate, isLoading: actionLoading } = useMutation(
    (formData) => isNew ? axiosPrivate.post(`/clients`, formData) : axiosPrivate.put(`/clients/${clientData.id}`, formData),
    {
      onSuccess: (res) => {
        const payload = res?.data?.data || {};
        if (payload?.secret) {
          setCreatedSecret(payload?.secret);
          setSecretModalOpen(true);
        } else {
          handleModalClose();
        }
        enqueueSnackbar(`Client successfully ${isNew ? 'created' : 'updated'}.`, { variant: "success" });
        refetch();
      },
      onError: (err) => {
        const message = err?.response?.data?.error?.message || "Operation failed";
        enqueueSnackbar(message, { variant: "error" });
      }
    }
  );

  const onSubmit = (data) => {
    const formattedData = { ...data, scopes: data.scopes.join(" ") };
    mutate(formattedData);
  };

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '600px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: theme => theme.palette.mode === 'light'
            ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
            : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh'
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', borderRadius: 2 }}>
              <AppsRounded />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{isNew ? "Register Client" : "Update Configuration"}</Typography>
          </Stack>
          <IconButton onClick={handleModalClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ p: 4, overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormSection title="Base Identity" icon={<AppsRounded fontSize="inherit" />}>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Name is required" }}
                      render={({ field }) => (
                        <TextField {...field} fullWidth size="small" label="Application Name" error={!!errors.name} helperText={errors.name?.message} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                      )}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Controller
                        name="client_type"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} select fullWidth size="small" label="Client Type" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}>
                            <MenuItem value="confidential">Confidential</MenuItem>
                            <MenuItem value="public">Public</MenuItem>
                          </TextField>
                        )}
                      />
                      <Controller
                        name="logo_url"
                        control={control}
                        rules={{ validate: validateURL }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Logo URL"
                            error={!!errors.logo_url}
                            helperText={errors.logo_url?.message}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                          />
                        )}
                      />
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <Controller
                        name="privacy_policy_url"
                        control={control}
                        rules={{ validate: validateURL }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Privacy Policy URL"
                            error={!!errors.privacy_policy_url}
                            helperText={errors.privacy_policy_url?.message}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                          />
                        )}
                      />
                      <Controller
                        name="terms_of_service_url"
                        control={control}
                        rules={{ validate: validateURL }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Terms of Service URL"
                            error={!!errors.terms_of_service_url}
                            helperText={errors.terms_of_service_url?.message}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </FormSection>
              </Grid>

              <Grid item xs={12}>
                <FormSection title="Access Control" icon={<Security fontSize="inherit" />}>
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="scopes"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small">
                          <InputLabel>Scopes</InputLabel>
                          <Select {...field} multiple label="Scopes" sx={{ borderRadius: 3 }}>
                            {scopesData?.data?.data?.map(s => <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Box>
                </FormSection>
              </Grid>

              <Grid item xs={12}>
                <FormSection title="Application Endpoints" icon={<Lan fontSize="inherit" />}>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.6 }}>REDIRECT URIs</Typography>
                    {fields.map((f, i) => (
                      <Stack key={f.id} direction="row" spacing={1}>
                        <Controller
                          name={`redirect_uris.${i}`}
                          control={control}
                          rules={{
                            required: "redirect_uris is required",
                            validate: validateURL,
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              placeholder="https://example.com/callback"
                              error={!!errors.redirect_uris?.[i]}
                              helperText={errors.redirect_uris?.[i]?.message}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                            />
                          )}
                        />
                        <IconButton onClick={() => remove(i)} disabled={fields.length === 1} color="error"><Delete fontSize="small" /></IconButton>
                      </Stack>
                    ))}
                    <Button startIcon={<Add />} onClick={() => append("")} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>Add Redirect URI</Button>
                  </Stack>
                </FormSection>
              </Grid>

              <Grid item xs={12}>
                <FormSection title="Logout Endpoints" icon={<Lan fontSize="inherit" />}>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.6 }}>POST-LOGOUT REDIRECT URIs</Typography>
                    {logoutFields.map((f, i) => (
                      <Stack key={f.id} direction="row" spacing={1}>
                        <Controller
                          name={`post_logout_redirect_uris.${i}`}
                          control={control}
                          rules={{
                            required: "At least one post logout redirect URI is required",
                            validate: validateURL,
                          }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              size="small"
                              placeholder="https://example.com/logout-callback"
                              error={!!errors.post_logout_redirect_uris?.[i]}
                              helperText={errors.post_logout_redirect_uris?.[i]?.message}
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                            />
                          )}
                        />
                        <IconButton onClick={() => removeLogout(i)} disabled={logoutFields.length === 1} color="error"><Delete fontSize="small" /></IconButton>
                      </Stack>
                    ))}
                    <Button startIcon={<Add />} onClick={() => appendLogout("")} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>Add Logout URI</Button>

                    <Box sx={{ mt: 1 }}>
                      <Controller
                        name="backchannel_logout_uri"
                        control={control}
                        rules={{
                          required: "backchannel logout uri is required",
                          validate: validateURL,
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Backchannel Logout URI"
                            placeholder="https://example.com/backchannel-logout"
                            error={!!errors.backchannel_logout_uri}
                            helperText={errors.backchannel_logout_uri?.message}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                          />
                        )}
                      />
                    </Box>
                  </Stack>
                </FormSection>
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button fullWidth variant="outlined" color="inherit" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
              <Button fullWidth variant="contained" type="submit" disabled={actionLoading} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none', boxShadow: theme => `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}` }}>
                {actionLoading ? <CircularProgress size={24} /> : (isNew ? "Register" : "Update")}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>

      <Dialog open={secretModalOpen} PaperProps={{ sx: { borderRadius: 6, p: 2 } }}>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 800 }}>Success!</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 3 }}>
            Client registered. Save the secret now. It won't be visible again.
          </Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 3, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ fontWeight: 800, fontFamily: 'monospace', wordBreak: 'break-all' }}>{createdSecret}</Typography>
            <IconButton onClick={() => navigator.clipboard.writeText(createdSecret)} size="small" color="primary"><ContentCopy /></IconButton>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button fullWidth variant="contained" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 800 }}>I have saved it</Button>
        </DialogActions>
      </Dialog>
    </Grid >
  );
}

function FormSection({ title, icon, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', fontSize: 18, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
      </Stack>
      {children}
    </Box>
  );
}

export default ClientsUpdate;
